import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Facebook, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
    "Fotografia Sportiva",
    "Ritratti & Editoriali",
    "Video & Montaggio",
    "Riprese con Drone",
    "Eventi & Matrimoni",
    "Grafica & Brand",
    "Altro",
];

export default function Contact({ onOpenPrivacy }) {
    const [form, setForm] = useState({ name: "", email: "", service: SERVICES[0], message: "" });
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Compila tutti i campi obbligatori.");
            return;
        }
        if (!consent) {
            toast.error("Devi accettare la privacy policy per continuare.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API}/contact`, { ...form, consent: true });
            if (res.data?.email_sent) {
                toast.success("Messaggio inviato. Ti risponderò a breve.");
            } else {
                toast.success("Messaggio ricevuto. Ti risponderò a breve.");
            }
            setForm({ name: "", email: "", service: SERVICES[0], message: "" });
            setConsent(false);
        } catch (err) {
            toast.error("Errore nell'invio. Riprova o scrivi su Instagram.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            className="relative bg-ink-950 border-t border-white/10 pt-20 md:pt-40 px-5 md:px-10"
            data-testid="contact-section"
        >
            <div className="overline mb-6">
                <span className="text-flash">■</span>&nbsp;&nbsp;04 — Let's work
            </div>
            <h2
                className="font-display uppercase text-bone-50 leading-[0.85] tracking-[-0.04em] break-words"
                style={{ fontSize: "clamp(2.25rem, 12vw, 14rem)" }}
                data-testid="contact-heading"
            >
                Facciamo<br />
                <span className="font-serif-i normal-case tracking-normal text-bone-400">qualcosa </span>
                di reale<span className="text-flash">.</span>
            </h2>

            <div className="mt-12 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
                {/* Left contacts */}
                <div className="md:col-span-4 order-2 md:order-1">
                    <div className="overline mb-4">Contatti diretti</div>
                    <div className="flex items-center gap-3 mb-8" data-testid="contact-socials">
                        <a
                            href="https://www.instagram.com/francescociotoliphoto/"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Instagram"
                            className="p-3 border border-white/15 rounded-full text-bone-50 hover:border-flash hover:text-flash transition-colors"
                            data-testid="contact-social-instagram"
                        >
                            <Instagram size={18} strokeWidth={1.5} />
                        </a>
                        <a
                            href="https://www.facebook.com/francescociotoli.photo"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Facebook"
                            className="p-3 border border-white/15 rounded-full text-bone-50 hover:border-flash hover:text-flash transition-colors"
                            data-testid="contact-social-facebook"
                        >
                            <Facebook size={18} strokeWidth={1.5} />
                        </a>
                        <a
                            href="mailto:ciotoli02@gmail.com"
                            aria-label="Email"
                            className="p-3 border border-white/15 rounded-full text-bone-50 hover:border-flash hover:text-flash transition-colors"
                            data-testid="contact-social-email"
                        >
                            <Mail size={18} strokeWidth={1.5} />
                        </a>
                    </div>
                    <ul className="space-y-3 text-bone-50 text-base md:text-lg">
                        <li>
                            <a href="mailto:ciotoli02@gmail.com" className="link-underline break-all" data-testid="contact-email">
                                ciotoli02@gmail.com
                            </a>
                        </li>
                        <li className="text-bone-400 font-light">Ceccano · Frosinone — IT</li>
                    </ul>

                    <div className="mt-10 md:mt-14">
                        <div className="overline mb-4">Disponibilità</div>
                        <p className="text-bone-200 font-light">
                            Aperto a nuovi <span className="font-serif-i text-bone-50">progetti</span> per{" "}
                            {new Date().getFullYear() + 1}. Rispondo entro 24 ore.
                        </p>
                    </div>
                </div>

                {/* Form */}
                <div className="md:col-span-8 order-1 md:order-2 relative">
                    <div className="absolute -top-3 left-6 z-10 px-2 bg-ink-950 overline text-flash text-[10px] md:text-[11px]">
                        ■ Modulo contatti
                    </div>
                    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 md:gap-y-6 border border-white/15 bg-ink-900/60 backdrop-blur-sm p-6 md:p-10" data-testid="contact-form">
                        <div>
                            <label htmlFor="cf-name" className="overline block mb-2 text-bone-50">Nome *</label>
                            <input
                                id="cf-name"
                                type="text"
                                value={form.name}
                                onChange={update("name")}
                                required
                                className="field"
                                placeholder="Il tuo nome"
                                data-testid="contact-input-name"
                            />
                        </div>
                        <div>
                            <label htmlFor="cf-email" className="overline block mb-2 text-bone-50">Email *</label>
                            <input
                                id="cf-email"
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                required
                                className="field"
                                placeholder="tu@dominio.com"
                                data-testid="contact-input-email"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="cf-service" className="overline block mb-2 text-bone-50">Servizio richiesto</label>
                            <select
                                id="cf-service"
                                value={form.service}
                                onChange={update("service")}
                                className="field"
                                data-testid="contact-input-service"
                            >
                                {SERVICES.map((s) => (
                                    <option key={s} value={s} className="bg-ink-900 text-bone-50">
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="cf-message" className="overline block mb-2 text-bone-50">Messaggio *</label>
                            <textarea
                                id="cf-message"
                                value={form.message}
                                onChange={update("message")}
                                required
                                className="field"
                                placeholder="Raccontami il tuo progetto…"
                                rows={5}
                                data-testid="contact-input-message"
                            />
                        </div>
                        <div className="md:col-span-2 mt-2">
                            <label className="flex items-start gap-3 cursor-pointer group" data-testid="contact-consent-wrap">
                                <input
                                    type="checkbox"
                                    checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)}
                                    className="mt-1 w-4 h-4 accent-flash flex-shrink-0 cursor-pointer"
                                    required
                                    data-testid="contact-consent-checkbox"
                                />
                                <span className="text-bone-400 font-light text-xs md:text-sm leading-relaxed group-hover:text-bone-200 transition-colors">
                                    Ho letto la{" "}
                                    <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); onOpenPrivacy?.(); }}
                                        className="text-flash link-underline"
                                        data-testid="contact-privacy-link"
                                    >
                                        Privacy Policy
                                    </button>{" "}
                                    e acconsento al trattamento dei miei dati personali per rispondere alla richiesta
                                    (art. 6.1.a GDPR). *
                                </span>
                            </label>
                        </div>
                        <div className="md:col-span-2 mt-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <p className="font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-bone-400">
                                * Campi obbligatori
                            </p>
                            <button type="submit" className="btn-flash w-full md:w-auto justify-center" disabled={loading} data-testid="contact-submit-button">
                                <span>{loading ? "Invio in corso…" : "Invia messaggio"}</span>
                                <span>→</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Footer */}
            <footer className="mt-20 md:mt-40 pt-8 pb-10 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-bone-400 font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase" data-testid="footer">
                <span>© {new Date().getFullYear()} Francesco Ciotoli · All rights reserved</span>
                <span>Photographer &amp; Videomaker · Ceccano (FR) IT</span>
                <a href="#hero" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="link-underline" data-testid="footer-back-to-top">
                    Back to top ↑
                </a>
            </footer>

            {/* SEO enrichment — indexable long-tail content */}
            <section className="pb-10 text-bone-600 text-xs md:text-sm leading-relaxed font-light border-t border-white/5 pt-8" data-testid="seo-block">
                <h2 className="sr-only">Francesco Ciotoli — Fotografo e Videomaker in Ciociaria</h2>
                <p className="max-w-4xl">
                    <strong className="text-bone-400">Francesco Ciotoli</strong> è un{" "}
                    <strong className="text-bone-400">fotografo professionista</strong> e{" "}
                    <strong className="text-bone-400">videomaker</strong> con base a{" "}
                    <strong className="text-bone-400">Ceccano</strong>, in provincia di{" "}
                    <strong className="text-bone-400">Frosinone</strong> ({" "}
                    <strong className="text-bone-400">Ciociaria</strong>, Lazio). Offre servizi di{" "}
                    fotografia sportiva, ritratti ed editoriali, riprese video cinematiche,
                    riprese aeree con drone (autorizzato ENAC), copertura di{" "}
                    matrimoni ed eventi, montaggio video e grafica in tutta Italia — con
                    particolare attenzione ai territori di Frosinone, Ceccano, Roma, Anagni,
                    Fiuggi, Cassino, Sora, Alatri e provincia. Fotografo ufficiale del{" "}
                    Ceccano Calcio. Per un preventivo scrivi a{" "}
                    <a href="mailto:ciotoli02@gmail.com" className="text-flash link-underline">
                        ciotoli02@gmail.com
                    </a>{" "}
                    o segui{" "}
                    <a href="https://www.instagram.com/francescociotoliphoto/" target="_blank" rel="noreferrer" className="text-flash link-underline">
                        @francescociotoliphoto
                    </a>{" "}
                    su Instagram.
                </p>
            </section>
            <div className="border-t border-white/5 py-6 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-center" data-testid="bcnext-credit">
                <button
                    onClick={() => onOpenPrivacy?.()}
                    className="text-bone-400 font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase link-underline hover:text-bone-50 transition-colors"
                    data-testid="footer-privacy-link"
                >
                    Privacy Policy
                </button>
                <span className="hidden md:inline text-bone-600">·</span>
                <span className="text-bone-400 font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase">
                    Realizzato da
                </span>
                <a
                    href="https://bcnext.it/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-bone-50 font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase link-underline"
                    data-testid="bcnext-link"
                >
                    <span className="text-flash">BC Next</span> — agenzia digitale della Ciociaria ↗
                </a>
            </div>
        </section>
    );
}
